import React, { useEffect, useState } from 'react';
import { Phone, Mail, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import { DeliveryDetailsForm } from './DeliveryDetailsForm';
import type { RouteJob } from '../../types/deliveryManagement';
import {
  isDeliveryRouteJobProductionComplete,
  getDeliveryStatusPanelClassNames,
  getProductionIncompleteCardClassNames,
} from '../../utils/deliveryRouteJobHelpers';
import {
  getJobStatusBadge,
  getAdminJobStatusLabel,
  getAdminJobStatusPillClassNames,
  getStatusBadgeOpacity,
} from '../../utils/jobStatusMapper';

interface BookingJobCardProps {
  job: RouteJob;
  index: number; // For sequence numbering
  onViewOrders: (jobId: string, customerName: string) => void;
}

export const BookingJobCard: React.FC<BookingJobCardProps> = ({
  job,
  index,
  onViewOrders,
}) => {
  const navigate = useNavigate();
  const customerName = job.customer_name || `${job.customer_first_name} ${job.customer_last_name}`.trim();

  const [displayStatus, setDisplayStatus] = useState<string>(job.delivery_status || '');

  useEffect(() => {
    setDisplayStatus(job.delivery_status || '');
  }, [job.id, job.delivery_status]);

  const statusInput = {
    status: job.status,
    production_status: job.production_status,
    fulfillment_type: job.fulfillment_type,
    delivery_result: job.delivery_result,
    collection_completed: job.collection_completed,
  };
  const badgeCode = getJobStatusBadge(statusInput);
  const fulfillmentLabel = getAdminJobStatusLabel(statusInput);
  const fulfillmentPillClass = getAdminJobStatusPillClassNames(statusInput);
  const cardOpacity = getStatusBadgeOpacity(badgeCode);

  // Format address
  const fullAddress =
    job.full_address ||
    [job.delivery_address_line1, job.delivery_address_line2].filter(Boolean).join(', ');

  // Extract phone and email from job if available
  const customerPhone = job.customer_mobile_number;
  const customerEmail = job.customer_email;

  const handlePostcodeClick = () => {
    navigate(`/jobs/${job.id}/edit`);
  };

  const isProductionComplete = isDeliveryRouteJobProductionComplete(job);

  const cardSurfaceClass = isProductionComplete
    ? `${getDeliveryStatusPanelClassNames(displayStatus)} hover:shadow-md`
    : `${getProductionIncompleteCardClassNames()} hover:shadow-md`;

  return (
    <div
      className={`rounded-lg border shadow-sm transition-all duration-200 relative ${cardSurfaceClass}`}
      style={{ opacity: cardOpacity }}
    >
      <div
        className="absolute top-2 right-2 z-10 px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        }}
        title={`Status: ${badgeCode}`}
      >
        {badgeCode}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-4 p-4">
        {/* Left Card Section */}
        <div className="space-y-3">
          {/* Sequence Number Badge */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">{index + 1}</span>
              </div>
            </div>

            {/* Postcode - Clickable */}
            <div className="flex-1 pr-10">
              <div
                className="text-2xl font-bold text-blue-600 hover:text-blue-800 mb-1 cursor-pointer transition-colors"
                onClick={handlePostcodeClick}
                title="Click to edit job"
              >
                {job.delivery_postcode}
              </div>
              <div className="text-sm text-gray-600">Job #{job.website_job_number}</div>
              <span
                className={`inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${fulfillmentPillClass}`}
              >
                {fulfillmentLabel}
              </span>
            </div>
          </div>

          {/* Address */}
          <div className="pl-14">
            <div className="text-sm font-medium text-gray-600 mb-0.5">Address</div>
            <div className="text-sm text-gray-800">{fullAddress}</div>
          </div>

          {/* Customer Details */}
          <div className="pl-14">
            <div className="text-sm font-medium text-gray-600 mb-1.5">Customer Details</div>
            <div className="space-y-1.5">
              <div className="text-base font-semibold text-gray-900">{customerName}</div>

              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                {customerPhone ? (
                  <a
                    href={`tel:${customerPhone}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {customerPhone}
                  </a>
                ) : (
                  <span className="text-gray-400 italic">No phone number</span>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                {customerEmail ? (
                  <a
                    href={`mailto:${customerEmail}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {customerEmail}
                  </a>
                ) : (
                  <span className="text-gray-400 italic">No email address</span>
                )}
              </div>
            </div>
          </div>

          <div className="pl-14 pt-1">
            <Button
              variant="warning"
              size="sm"
              icon={<Package className="w-4 h-4" />}
              onClick={() => onViewOrders(job.id, customerName)}
            >
              View Orders
            </Button>
          </div>
        </div>

        {/* Right Card Section - Delivery Details */}
        <div className="border-l border-black/10 pl-3 hidden md:block">
          <DeliveryDetailsForm
            jobId={job.id}
            initialData={{
              delivery_window_start: job.delivery_window_start,
              delivery_window_end: job.delivery_window_end,
              delivery_status: job.delivery_status,
              delivery_notes: job.delivery_notes,
            }}
            potentialDeliveryDate={job.potential_delivery_date}
            deliveryScheduleEmailSentAt={job.delivery_schedule_email_sent_at}
            deliveryScheduleEmailSentByUsername={job.delivery_schedule_email_sent_by_username}
            onStatusChange={setDisplayStatus}
            onSave={(data) => setDisplayStatus(data.delivery_status || '')}
          />
        </div>
      </div>
    </div>
  );
};

BookingJobCard.displayName = 'BookingJobCard';
